import {Three as THREE, ThreeTsl as TSL} from "#material/three/exports"
import type {Vec2, Vec3, Vec4} from "#material/types/vector"
import type {Color} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export const threeRGBColor = (color: Color): THREE.Node<"color"> => {
    return TSL.color(new THREE.Color(color.r, color.g, color.b))
}

export const threeValue = (value: number): THREE.Node<"float"> => {
    return TSL.float(value)
}

export const threeVec2 = (value: Vec2): THREE.Node<"vec2"> => {
    return TSL.vec2(new THREE.Vector2(value.x, value.y))
}

export const threeVec3 = (value: Vec3): THREE.Node<"vec3"> => {
    return TSL.vec3(new THREE.Vector3(value.x, value.y, value.z))
}

export const threeVec4 = (value: Vec4): THREE.Node<"vec4"> => {
    return TSL.vec4(new THREE.Vector4(value.x, value.y, value.z, value.w))
}

// At runtime every TSL node supports these conversions, but @types/three r184 narrowed the methods to specific node
// type parameters — so nodes typed as `Node<unknown>` (e.g. BumpMapNode, NormalMapNode, material.emissiveNode) can't
// reach them directly. These helpers localize the cast so call sites stay clean.
export const asVec3 = (node: THREE.Node): THREE.Node<"vec3"> => (node as THREE.Node<"vec3">).toVec3()
export const asFloat = (node: THREE.Node): THREE.Node<"float"> => (node as THREE.Node<"float">).toFloat()

export const threeNormal = (color: Color = {r: 0.5, g: 0.5, b: 1.0}): THREE.Node<"vec3"> => asVec3(TSL.normalMap(threeRGBColor(color)))

export const threeNormalFromVec3 = (value: Vec3): THREE.Node<"vec3"> | undefined => {
    if (value.x === 0 && value.y === 0 && value.z === 0) return undefined
    const r = (value.x + 1) / 2
    const g = (value.y + 1) / 2
    const b = (value.z + 1) / 2
    return threeNormal({
        r,
        g,
        b,
    })
}

export const threeConvert = <T, R>(value: T | undefined | null, converter: (value: T) => R, checker?: (value: T) => boolean): R | undefined => {
    if (value === undefined) return undefined
    if (value === null) return undefined
    if (checker !== undefined && !checker(value)) return undefined
    return converter(value)
}

export const threeColorBurn = TSL.Fn(([inputA, inputB, f]: [inputA: THREE.Node<"color">, inputB: THREE.Node<"color">, f: THREE.Node<"float">]) => {
    const resultColor = TSL.vec3(0).toVar()

    // Process R component
    const tmpR = TSL.float(1.0).sub(f).add(f.mul(inputB.r)).toVar()
    TSL.If(tmpR.lessThanEqual(0.0), () => {
        resultColor.r.assign(0.0)
    })
        .ElseIf(TSL.float(1.0).sub(TSL.float(1.0).sub(inputA.r).div(tmpR)).lessThan(0.0), () => {
            resultColor.r.assign(0.0)
        })
        .Else(() => {
            const computed = TSL.float(1.0).sub(TSL.float(1.0).sub(inputA.r).div(tmpR))
            resultColor.r.assign(TSL.clamp(computed, 0.0, 1.0))
        })

    // Process G component
    const tmpG = TSL.float(1.0).sub(f).add(f.mul(inputB.g)).toVar()
    TSL.If(tmpG.lessThanEqual(0.0), () => {
        resultColor.g.assign(0.0)
    })
        .ElseIf(TSL.float(1.0).sub(TSL.float(1.0).sub(inputA.g).div(tmpG)).lessThan(0.0), () => {
            resultColor.g.assign(0.0)
        })
        .Else(() => {
            const computed = TSL.float(1.0).sub(TSL.float(1.0).sub(inputA.g).div(tmpG))
            resultColor.g.assign(TSL.clamp(computed, 0.0, 1.0))
        })

    // Process B component
    const tmpB = TSL.float(1.0).sub(f).add(f.mul(inputB.b)).toVar()
    TSL.If(tmpB.lessThanEqual(0.0), () => {
        resultColor.b.assign(0.0)
    })
        .ElseIf(TSL.float(1.0).sub(TSL.float(1.0).sub(inputA.b).div(tmpB)).lessThan(0.0), () => {
            resultColor.b.assign(0.0)
        })
        .Else(() => {
            const computed = TSL.float(1.0).sub(TSL.float(1.0).sub(inputA.b).div(tmpB))
            resultColor.b.assign(TSL.clamp(computed, 0.0, 1.0))
        })

    return resultColor
})

export const threeColorDodge = TSL.Fn(([inputA, inputB, f]: [inputA: THREE.Node<"color">, inputB: THREE.Node<"color">, f: THREE.Node<"float">]) => {
    const resultColor = inputA.toVar()

    // Process R component
    TSL.If(inputA.r.notEqual(0.0), () => {
        const tmp = TSL.float(1.0).sub(f.mul(inputB.r)).toVar()
        TSL.If(tmp.lessThanEqual(0.0), () => {
            resultColor.r.assign(1.0)
        })
            .ElseIf(inputA.r.div(tmp).greaterThan(1.0), () => {
                resultColor.r.assign(1.0)
            })
            .Else(() => {
                resultColor.r.assign(inputA.r.div(tmp))
            })
    })

    // Process G component
    TSL.If(inputA.g.notEqual(0.0), () => {
        const tmp = TSL.float(1.0).sub(f.mul(inputB.g)).toVar()
        TSL.If(tmp.lessThanEqual(0.0), () => {
            resultColor.g.assign(1.0)
        })
            .ElseIf(inputA.g.div(tmp).greaterThan(1.0), () => {
                resultColor.g.assign(1.0)
            })
            .Else(() => {
                resultColor.g.assign(inputA.g.div(tmp))
            })
    })

    // Process B component
    TSL.If(inputA.b.notEqual(0.0), () => {
        const tmp = TSL.float(1.0).sub(f.mul(inputB.b)).toVar()
        TSL.If(tmp.lessThanEqual(0.0), () => {
            resultColor.b.assign(1.0)
        })
            .ElseIf(inputA.b.div(tmp).greaterThan(1.0), () => {
                resultColor.b.assign(1.0)
            })
            .Else(() => {
                resultColor.b.assign(inputA.b.div(tmp))
            })
    })

    return resultColor
})

export const threeOverlay = TSL.Fn(([inputA, inputB, f]: [inputA: THREE.Node<"color">, inputB: THREE.Node<"color">, f: THREE.Node<"float">]) => {
    const resultColor = TSL.vec3(0).toVar()
    const invF = TSL.float(1.0).sub(f)

    // Process R component
    TSL.If(inputA.r.lessThan(0.5), () => {
        resultColor.r.assign(inputA.r.mul(invF.add(TSL.float(2.0).mul(f).mul(inputB.r))))
    }).Else(() => {
        resultColor.r.assign(TSL.float(1.0).sub(invF.add(TSL.float(2.0).mul(f).mul(TSL.float(1.0).sub(inputB.r))).mul(TSL.float(1.0).sub(inputA.r))))
    })

    // Process G component
    TSL.If(inputA.g.lessThan(0.5), () => {
        resultColor.g.assign(inputA.g.mul(invF.add(TSL.float(2.0).mul(f).mul(inputB.g))))
    }).Else(() => {
        resultColor.g.assign(TSL.float(1.0).sub(invF.add(TSL.float(2.0).mul(f).mul(TSL.float(1.0).sub(inputB.g))).mul(TSL.float(1.0).sub(inputA.g))))
    })

    // Process B component
    TSL.If(inputA.b.lessThan(0.5), () => {
        resultColor.b.assign(inputA.b.mul(invF.add(TSL.float(2.0).mul(f).mul(inputB.b))))
    }).Else(() => {
        resultColor.b.assign(TSL.float(1.0).sub(invF.add(TSL.float(2.0).mul(f).mul(TSL.float(1.0).sub(inputB.b))).mul(TSL.float(1.0).sub(inputA.b))))
    })

    return resultColor
})

export const threeRgbToHsv = TSL.Fn(([rgb]: [rgb: THREE.Node<"color">]) => {
    const hsv = TSL.vec3(0).toVar()
    const rgbmax = TSL.max(rgb.r, TSL.max(rgb.g, rgb.b))
    const rgbmin = TSL.min(rgb.r, TSL.min(rgb.g, rgb.b))
    const delta = rgbmax.sub(rgbmin)

    hsv.z.assign(rgbmax)

    TSL.If(rgbmax.equal(0.0), () => {
        hsv.assign(TSL.vec3(0.0, 0.0, 0.0))
    }).Else(() => {
        hsv.y.assign(delta.div(rgbmax))
    })

    const c = TSL.vec3(rgbmax, rgbmax, rgbmax).sub(rgb.rgb).div(delta).toVar()

    TSL.If(hsv.y.equal(0.0), () => {
        hsv.x.assign(0.0)
    }).Else(() => {
        TSL.If(rgb.r.equal(rgbmax), () => {
            hsv.x.assign(c.z.sub(c.y))
        })
            .ElseIf(rgb.g.equal(rgbmax), () => {
                hsv.x.assign(TSL.float(2.0).add(c.x).sub(c.z))
            })
            .Else(() => {
                hsv.x.assign(TSL.float(4.0).add(c.y).sub(c.x))
            })

        hsv.x.assign(hsv.x.div(6.0))

        TSL.If(hsv.x.lessThan(0.0), () => {
            hsv.x.assign(hsv.x.add(1.0))
        })
    })

    return hsv
})

export const threeHsvToRgb = TSL.Fn(([hsv]: [hsv: THREE.Node<"vec3">]) => {
    const rgb = TSL.vec3(0).toVar()
    const h = hsv.x.toVar()
    const s = hsv.y
    const v = hsv.z

    TSL.If(s.equal(0.0), () => {
        rgb.assign(TSL.vec3(v, v, v))
    }).Else(() => {
        TSL.If(h.equal(1.0), () => {
            h.assign(0.0)
        })

        h.assign(h.mul(6.0))
        const i = TSL.floor(h)
        const f = h.sub(i)

        const p = v.mul(TSL.float(1.0).sub(s))
        const q = v.mul(TSL.float(1.0).sub(s.mul(f)))
        const t = v.mul(TSL.float(1.0).sub(s.mul(TSL.float(1.0).sub(f))))

        TSL.If(i.equal(0.0), () => {
            rgb.assign(TSL.vec3(v, t, p))
        })
            .ElseIf(i.equal(1.0), () => {
                rgb.assign(TSL.vec3(q, v, p))
            })
            .ElseIf(i.equal(2.0), () => {
                rgb.assign(TSL.vec3(p, v, t))
            })
            .ElseIf(i.equal(3.0), () => {
                rgb.assign(TSL.vec3(p, q, v))
            })
            .ElseIf(i.equal(4.0), () => {
                rgb.assign(TSL.vec3(t, p, v))
            })
            .Else(() => {
                rgb.assign(TSL.vec3(v, p, q))
            })
    })

    return rgb.toColor()
})

export const lutSize = 256
const lutOffset = 0.5 / lutSize

export type ThreeFloatTextureType = typeof THREE.HalfFloatType | typeof THREE.FloatType

export function createLutTypedArray(data: number[], type: ThreeFloatTextureType): Float32Array | Uint16Array {
    if (type === THREE.HalfFloatType) {
        return Uint16Array.from(data, THREE.DataUtils.toHalfFloat)
    }
    return new Float32Array(data)
}

const applyLut = TSL.Fn(
    ([rgbaIn, lut, offset, fac]: [rgbaIn: THREE.Node<"vec4">, lut: THREE.TextureNode, offset: THREE.Node<"float">, fac: THREE.Node<"float">]) => {
        const rgbaOut = TSL.vec4(
            lut.sample(TSL.vec2(rgbaIn.r.add(lutOffset), offset)).r,
            lut.sample(TSL.vec2(rgbaIn.g.add(lutOffset), offset)).g,
            lut.sample(TSL.vec2(rgbaIn.b.add(lutOffset), offset)).b,
            lut.sample(TSL.vec2(rgbaIn.a.add(lutOffset), offset)).a,
        )

        return TSL.mix(rgbaIn, rgbaOut, fac)
    },
)

export class ThreeApplyLUTNode extends THREE.Node<"vec4"> {
    constructor(
        public rgbaInput: THREE.Node<"vec4">,
        public lutTexture: THREE.DataTexture,
        public index: number,
        public fac: THREE.Node<"float">,
    ) {
        super("vec4")

        if (this.lutTexture.image.width !== lutSize) {
            throw new Error(`LUT texture must be of width ${lutSize}, but got ${this.lutTexture.image.width}`)
        }

        if (this.index < 0 || this.index >= this.lutTexture.image.height) {
            throw new Error(`LUT texture index must be between 0 and ${this.lutTexture.image.height - 1}, but got ${this.index}`)
        }
    }

    override setup() {
        const lutTextureNode = TSL.texture(this.lutTexture)
        const offsetValue = (this.index + 0.5) / this.lutTexture.image.height

        return applyLut(this.rgbaInput, lutTextureNode, offsetValue, this.fac)
    }
}

export const rgbBlendType = z.enum([
    "MIX",
    "ADD",
    "SUBTRACT",
    "MULTIPLY",
    "SCREEN",
    "DIVIDE",
    "DIFFERENCE",
    "EXCLUSION",
    "DARKEN",
    "LIGHTEN",
    "OVERLAY",
    "DODGE",
    "BURN",
    "HUE",
    "SATURATION",
    "VALUE",
    "COLOR",
    "SOFT_LIGHT",
    "LINEAR_LIGHT",
])

export type RgbBlendType = z.infer<typeof rgbBlendType>

export const getColor = (
    operation: RgbBlendType,
    inputA: THREE.Node<"color">,
    inputB: THREE.Node<"color">,
    facNode: THREE.Node<"float">,
): THREE.Node<"color"> => {
    const vecA = inputA.rgb
    const vecB = inputB.rgb

    if (operation === "MIX") {
        return TSL.mix(inputA, inputB, facNode).toColor()
    } else if (operation === "LINEAR_LIGHT") {
        const mulBNode = TSL.mul(vecB, threeValue(2.0))
        const subBNode = TSL.sub(mulBNode, threeValue(1))
        const facBNode = TSL.mul(facNode, subBNode)
        return TSL.color(TSL.add(vecA, facBNode))
    } else if (operation === "SOFT_LIGHT") {
        const invANode = TSL.sub(threeValue(1), vecA) // (1-A)
        const invBNode = TSL.sub(threeValue(1), vecB) // (1-B)
        const mulInvAInvBNode = TSL.mul(invANode, invBNode) // (1-A)(1-B)
        const screenNode = TSL.sub(threeValue(1), mulInvAInvBNode) // 1-(1-A)(1-B)
        const mulInvABNode = TSL.mul(invANode, vecB) // (1-A)B
        const mulInvABANode = TSL.mul(mulInvABNode, vecA) // (1-A)BA
        const mulAScreenNode = TSL.mul(vecA, screenNode) // A*scr
        const additionNode = TSL.add(mulInvABANode, mulAScreenNode) // (1-A)BA+A*scr
        return TSL.mix(inputA, additionNode, facNode).toColor() // (1-f)A+f[(1-A)BA+A*scr]
    } else if (operation === "MULTIPLY") {
        const mulNode = TSL.mul(vecA, vecB)
        return TSL.mix(inputA, mulNode, facNode).toColor()
    } else if (operation === "ADD") {
        const mulNode = TSL.add(vecA, vecB)
        return TSL.mix(inputA, mulNode, facNode).toColor()
    } else if (operation === "SUBTRACT") {
        const mulNode = TSL.sub(vecA, vecB)
        return TSL.mix(inputA, mulNode, facNode).toColor()
    } else if (operation === "DIFFERENCE") {
        const subNode = TSL.sub(vecA, vecB) // A-B
        const diffNode = TSL.abs(subNode) // |A-B|
        return TSL.mix(inputA, diffNode, facNode).toColor() // (1-f)A+f|A-B|
    } else if (operation === "EXCLUSION") {
        const addNode = TSL.add(vecA, vecB) // A+B
        const mulNode = TSL.mul(threeValue(2), TSL.mul(vecA, vecB)) // 2*A*B
        const exclusionNode = TSL.sub(addNode, mulNode) // A+B-2*A*B
        return TSL.max(TSL.mix(inputA, exclusionNode, facNode), threeValue(0)).toColor() // max((1-f)A+f*[A+B-2*A*B], 0)
    } else if (operation === "LIGHTEN") {
        const maxNode = TSL.max(vecA, vecB) // max(A, B)
        return TSL.mix(inputA, maxNode, facNode).toColor() // (1-f)A+f*max(A,B)
    } else if (operation === "DARKEN") {
        const minNode = TSL.min(vecA, vecB) // min(A, B)
        return TSL.mix(inputA, minNode, facNode).toColor() // (1-f)A+f*min(A,B)
    } else if (operation === "DIVIDE") {
        const rNodeInpA = inputA.r
        const gNodeInpA = inputA.g
        const bNodeInpA = inputA.b
        const rNodeInpB = inputB.r
        const gNodeInpB = inputB.g
        const bNodeInpB = inputB.b
        const divNodeR = TSL.div(rNodeInpA, rNodeInpB)
        const divNodeG = TSL.div(gNodeInpA, gNodeInpB)
        const divNodeB = TSL.div(bNodeInpA, bNodeInpB)
        const divResultR = TSL.select(rNodeInpB.notEqual(threeValue(0)), divNodeR, rNodeInpA)
        const divResultG = TSL.select(gNodeInpB.notEqual(threeValue(0)), divNodeG, gNodeInpA)
        const divResultB = TSL.select(bNodeInpB.notEqual(threeValue(0)), divNodeB, bNodeInpA)
        const divNode = TSL.vec3(divResultR, divResultG, divResultB)

        return TSL.mix(inputA, divNode, facNode).toColor()
    } else if (operation === "SCREEN") {
        const invANode = TSL.sub(threeValue(1), vecA) // (1-A)
        const invBNode = TSL.sub(threeValue(1), vecB) // (1-B)
        const invFacNode = TSL.sub(threeValue(1), facNode) // (1-f)
        const mulInvFacInvANode = TSL.mul(invFacNode, invANode) // (1-f)(1-A)
        const mulFacInvANode = TSL.mul(facNode, invANode) // f(1-A)
        const mulInvAInvBNode = TSL.mul(mulFacInvANode, invBNode) // f(1-A)(1-B)
        const addNode = TSL.add(mulInvFacInvANode, mulInvAInvBNode) // (1-f)(1-A)+f(1-A)(1-B)
        return TSL.color(TSL.sub(threeValue(1), addNode)) // 1-[(1-f)(1-A)+f(1-A)(1-B)]
    } else if (operation === "COLOR") {
        const hsvNodeInpB = threeRgbToHsv(inputB)
        const sNodeHsvInpB = hsvNodeInpB.y

        // If part
        const hsvNodeInpA = threeRgbToHsv(inputA)
        const hNodeHsvInpB = hsvNodeInpB.x
        const vNodeHsvInpA = hsvNodeInpA.z
        const newHsvNode = TSL.vec3(hNodeHsvInpB, sNodeHsvInpB, vNodeHsvInpA)
        const newRgbNode = threeHsvToRgb(newHsvNode)
        const ifNode = TSL.mix(vecA, newRgbNode, facNode)

        return TSL.select(TSL.greaterThan(sNodeHsvInpB, threeValue(0)), ifNode, vecA).toColor()
    } else if (operation === "HUE") {
        const hsvNodeInpB = threeRgbToHsv(inputB)
        const sNodeHsvInpB = hsvNodeInpB.y

        // If part
        const hsvNodeInpA = threeRgbToHsv(inputA)
        const hNodeHsvInpB = hsvNodeInpB.x
        const sNodeHsvInpA = hsvNodeInpA.y
        const vNodeHsvInpA = hsvNodeInpA.z
        const newHsvNode = TSL.vec3(hNodeHsvInpB, sNodeHsvInpA, vNodeHsvInpA)
        const newNodeRgb = threeHsvToRgb(newHsvNode)
        const ifNode = TSL.mix(vecA, newNodeRgb, facNode)

        return TSL.select(TSL.greaterThan(sNodeHsvInpB, threeValue(0)), ifNode, vecA).toColor()
    } else if (operation === "SATURATION") {
        const hsvNodeInpA = threeRgbToHsv(inputA)
        const sNodeHsvInpA = hsvNodeInpA.y

        // If part
        const hsvNodeInpB = threeRgbToHsv(inputB)
        const hNodeHsvInpA = hsvNodeInpA.x
        const vNodeHsvInpA = hsvNodeInpA.z
        const sNodeHsvInpB = hsvNodeInpB.y
        const mixNode = TSL.mix(sNodeHsvInpA, sNodeHsvInpB, facNode)
        const ifNodeHsv = TSL.vec3(hNodeHsvInpA, mixNode, vNodeHsvInpA)
        const ifNodeRgb = threeHsvToRgb(ifNodeHsv)

        return TSL.select(TSL.greaterThan(sNodeHsvInpA, threeValue(0)), ifNodeRgb, vecA).toColor()
    } else if (operation === "VALUE") {
        const hsvNodeInpA = threeRgbToHsv(inputA)
        const hsvNodeInpB = threeRgbToHsv(inputB)
        const hNodeHsvInpA = hsvNodeInpA.x
        const sNodeHsvInpA = hsvNodeInpA.y
        const vNodeHsvInpA = hsvNodeInpA.z
        const vNodeHsvInpB = hsvNodeInpB.z
        const mixNode = TSL.mix(vNodeHsvInpA, vNodeHsvInpB, facNode)
        const newHsvNode = TSL.vec3(hNodeHsvInpA, sNodeHsvInpA, mixNode)

        return threeHsvToRgb(newHsvNode)
    } else if (operation === "BURN") {
        return threeColorBurn(inputA, inputB, facNode).toColor()
    } else if (operation === "DODGE") {
        return threeColorDodge(inputA, inputB, facNode).toColor()
    } else if (operation === "OVERLAY") {
        return threeOverlay(inputA, inputB, facNode).toColor()
    } else throw new Error(`Unsupported operation: ${operation}`)
}
